import React, {useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography} from "@mui/material";
import {OverviewSettings} from "arteco-api-client-ts";
import {btnSx} from "../../../../components/kit";

interface Field {
	key: keyof OverviewSettings;
	label: string;
	unit: string;
	rule: string;
}

const FIELDS: Field[] = [
	{key: "minMarginPct", label: "Margen bruto mínimo", unit: "%", rule: "Verde si llega; ámbar a menos de 5 puntos; rojo por debajo. También mide la previsión de cierre."},
	{key: "maxCollectionDays", label: "Días de cobro máximos", unit: "días", rule: "Verde por debajo; ámbar hasta 10 días por encima; rojo más allá."},
	{key: "maxCustomerPct", label: "Peso máximo de un cliente", unit: "%", rule: "Verde por debajo; ámbar hasta 10 puntos por encima; rojo más allá."},
	{key: "yearlyHours", label: "Horas de convenio al año", unit: "h", rule: "Base del coste estándar por hora y de la capacidad de cada persona."},
	{key: "referenceHourRate", label: "Tarifa de referencia", unit: "€/h", rule: "Con la que se compara el coste por hora de una línea."},
];

/** Objetivos y parámetros de la visión general (#14497). Valen para todos los periodos (ISO 9001 §9.1.3). */
export const SettingsDialog = ({open, value, onClose, onSave}: {
	open: boolean, value: OverviewSettings, onClose: () => void, onSave: (s: OverviewSettings) => Promise<unknown>,
}) => {
	const [draft, setDraft] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setDraft(Object.fromEntries(FIELDS.map(f => [f.key, String(value[f.key] ?? "")])));
		}
	}, [open, value]);

	const parsed = Object.fromEntries(FIELDS.map(f => [f.key, Number((draft[f.key] || "").replace(",", "."))]));
	const invalid = FIELDS.filter(f => !(parsed[f.key] > 0) || ((f.unit === "%") && parsed[f.key] > 100)).map(f => f.key);

	const save = () => {
		setSaving(true);
		onSave(parsed as OverviewSettings).then(onClose).finally(() => setSaving(false));
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle sx={{fontWeight: 700}}>Objetivos y parámetros</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
					Valen para todos los periodos. Facturado, vencido y aceptado sin facturar quedan sin objetivo hasta que se fije uno.
				</Typography>
				<Box sx={{display: "grid", gap: 2}}>
					{FIELDS.map(f => (
						<TextField key={f.key} label={f.label} size="small" value={draft[f.key] ?? ""}
								   onChange={e => setDraft({...draft, [f.key]: e.target.value})}
								   error={invalid.includes(f.key)} helperText={f.rule}
								   inputProps={{inputMode: "decimal"}}
								   InputProps={{endAdornment: <InputAdornment position="end">{f.unit}</InputAdornment>}}/>
					))}
				</Box>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2}}>
				<Button onClick={onClose} sx={btnSx}>Cancelar</Button>
				<Button variant="contained" onClick={save} disabled={saving || invalid.length > 0} sx={btnSx}>Guardar objetivos</Button>
			</DialogActions>
		</Dialog>
	);
};
